// The feature registry — one entry per gated page the full-PMS sprint adds.
//
// Sidebar.js renders an entry when `features[entry.feature]` is true; App.js renders
// its Page for that `activePage`, or <FeatureOff/> when the feature is off (a deep
// link to a hidden page). Adding a page to Atlas means adding a line here, not
// editing three files.
//
// OWNERSHIP (full-PMS sprint): Instance A owns this file and NOBODY ELSE EDITS IT —
// not to swap an import, not to delete a stub. If you own a slot, replace the BODY of
// your own `Stub.js` with your page, or re-export it from there:
//
//     export { default } from './PatientsPage';
//
// and this file keeps working untouched. Two strands deleted their stub and edited the
// import here instead; that turns one file into a six-way merge conflict for a change
// that was never needed. The {id, label, icon, feature, adminOnly} shape is what the
// sidebar, the URL sync and the gate all read, and `id` is in the URL
// (/p/<practiceId>/<id>), so renaming one breaks a biller's bookmark.
//
// `feature` must be a key in the backend's capabilities_service.FEATURES. There is a
// test for that in the backend suite; the frontend just trusts the map it is given.

import React from 'react';

import PatientsStub from './patients/Stub';
import AuthorizationsStub from './authorizations/Stub';
import WorklistStub from './money/WorklistStub';
import StatementsStub from './money/StatementsStub';
import ClaimEditorStub from './claims/ClaimEditorStub';
import FeeScheduleStub from './claims/FeeScheduleStub';
import QueuePage, { queueIcon } from './queue';
import { CoveragePage, coverageIcon } from './queue';
import CollectionsPage, { collectionsIcon } from './collections';
import PatientBalancesPage, { patientBalancesIcon } from './patientBalances';
import ChargesPage, { chargesIcon } from './charges';
import ReportsPage, { reportsIcon } from './reports';
import DataPage, { dataIcon } from './data';

const patientsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const authorizationsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

const worklistIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 6h11" />
    <path d="M9 12h11" />
    <path d="M9 18h11" />
    <circle cx="4.5" cy="6" r="1.5" />
    <circle cx="4.5" cy="12" r="1.5" />
    <circle cx="4.5" cy="18" r="1.5" />
  </svg>
);

const statementsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 2h9l5 5v15a0 0 0 0 1 0 0H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
    <path d="M15 2v5h5" />
    <path d="M9 13h6" />
    <path d="M9 17h4" />
  </svg>
);

const claimEditorIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const feeScheduleIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M9 9v12" />
  </svg>
);

const FEATURE_PAGES = [
  {
    id: 'patients',
    label: 'Patients',
    icon: patientsIcon,
    feature: 'patients',
    adminOnly: false,
    Page: PatientsStub,
  },
  {
    id: 'authorizations',
    label: 'Authorizations',
    icon: authorizationsIcon,
    feature: 'authorizations',
    adminOnly: false,
    Page: AuthorizationsStub,
  },
  {
    id: 'worklist',
    label: 'Worklist',
    icon: worklistIcon,
    feature: 'worklist',
    adminOnly: false,
    Page: WorklistStub,
  },
  {
    id: 'statements',
    label: 'Statements',
    icon: statementsIcon,
    feature: 'statements',
    adminOnly: false,
    Page: StatementsStub,
  },
  {
    // Collections sprint, Instance B. `collections` is on exactly when `statements` is.
    id: 'collections',
    label: 'Collections',
    icon: collectionsIcon,
    feature: 'collections',
    adminOnly: false,
    Page: CollectionsPage,
  },
  {
    // Outreach PR 3: the backlog worklist with bulk text / email campaigns. Rides
    // `collections` like the Outreach card; staff only (Sidebar's CLIENT_PAGES
    // leaves it out).
    id: 'patient-balances',
    label: 'Patient balances',
    icon: patientBalancesIcon,
    feature: 'collections',
    adminOnly: false,
    Page: PatientBalancesPage,
  },
  {
    // Collections sprint, Instance B: capture drafts that need a person, visits
    // without a charge. `charge_templates` is on exactly when `charge_capture` is.
    id: 'charges',
    label: 'Charges',
    icon: chargesIcon,
    feature: 'charge_templates',
    adminOnly: false,
    Page: ChargesPage,
  },
  {
    id: 'claim-editor',
    label: 'Claim Editor',
    icon: claimEditorIcon,
    feature: 'claim_editor',
    // Edits every CMS-1500 field on a claim that is about to be billed. Admin-tier
    // until Instance F says otherwise.
    adminOnly: true,
    Page: ClaimEditorStub,
  },
  {
    id: 'fee-schedule',
    label: 'Fee Schedule',
    icon: feeScheduleIcon,
    feature: 'fee_schedule',
    adminOnly: true,
    Page: FeeScheduleStub,
  },
  {
    // Submit-queue sprint, Instance B (the workplan's one `queue` entry).
    id: 'queue',
    label: 'Submit queue',
    icon: queueIcon,
    feature: 'submit_queue',
    adminOnly: false,
    Page: QueuePage,
  },
  {
    // Brief 15: the PMS coverage matrix — what the practice's portal runbook lets
    // Atlas do, per contract operation. Same gate as the queue; the page itself says
    // "nothing to show" for a queue practice with no submit portal.
    id: 'pms-coverage',
    label: 'PMS coverage',
    icon: coverageIcon,
    feature: 'submit_queue',
    adminOnly: false,
    Page: CoveragePage,
  },
  {
    // Setup > Data (2026-09-20). Where the practice's data comes from, when it last
    // arrived, and the template CSV for a practice Atlas has no connector for. Sits in
    // the Setup group beside Practice Details (Sidebar.js NAV_GROUPS). `data_page` needs
    // no capability, so this entry is visible for every practice — deliberately: the
    // practice with nothing connected is the one whose biller needs the page.
    id: 'data',
    label: 'Data',
    icon: dataIcon,
    feature: 'data_page',
    adminOnly: false,
    Page: DataPage,
  },
  {
    // Collections sprint, Instance D (ruling O3).
    id: 'reports',
    label: 'Reports',
    icon: reportsIcon,
    feature: 'reports',
    adminOnly: false,
    Page: ReportsPage,
  },
];

export default FEATURE_PAGES;

export const FEATURE_PAGE_IDS = FEATURE_PAGES.map((entry) => entry.id);

export function findFeaturePage(pageId) {
  return FEATURE_PAGES.find((entry) => entry.id === pageId) || null;
}
